import {
  SheetNode,
  SheetSkillsList,
  CascadeNodeType,
  CascadeData,
  ImportedSheetData,
} from './types'

/**
 * Array of all cascade node types in display order
 */
export const CASCADE_NODE_TYPES: CascadeNodeType[] = [
  'course',
  'module',
  'unit',
  'document',
  'lesson_plan',
  'activity',
  'assessment',
  'question',
]

/**
 * Display name for cascade node types in the cascade UI.
 * Returns 'document (section)' for 'document', raw type string for all others.
 */
export function cascadeDisplayName(type: string): string {
  if (type === 'document') {
    return 'document (section)'
  }
  return type
}

/**
 * Whether a "To" type is valid for cascade given "From" type and mode.
 * Uses static CASCADE_NODE_TYPES order only (not dynamic cascade order).
 * Roll Up: To must be higher in hierarchy (lower index). Roll Down: To must be lower (higher index).
 */
export function isToTypeValidForCascade(
  toTypeValue: string,
  fromTypeValue: string,
  modeValue: string,
): boolean {
  if (!toTypeValue || !fromTypeValue) {
    return false
  }
  const fromIndex = CASCADE_NODE_TYPES.indexOf(fromTypeValue as CascadeNodeType)
  const toIndex = CASCADE_NODE_TYPES.indexOf(toTypeValue as CascadeNodeType)
  if (fromIndex === -1 || toIndex === -1) {
    return false
  }
  if (modeValue === 'Roll Up') {
    return toIndex < fromIndex
  }
  if (modeValue === 'Roll Down') {
    return toIndex > fromIndex
  }
  return false
}

/**
 * Detect the cascade hierarchy order from the actual nodeList structure.
 *
 * Phase 1: Find fixed top-level types (course, module, unit) by first occurrence.
 * Phase 2: Scan forward from after the last top-level type, registering new cascade
 *          types until a module/unit scope boundary is hit.
 *
 * @param nodeList - Flat list of SheetNodes in spreadsheet order
 * @returns Ordered array of CascadeNodeType representing the detected hierarchy
 */
export function detectCascadeOrder(nodeList: SheetNode[]): CascadeNodeType[] {
  if (nodeList.length === 0) {
    return []
  }

  const result: CascadeNodeType[] = []
  const seen = new Set<CascadeNodeType>()
  const cascadeTypeSet = new Set<string>(CASCADE_NODE_TYPES)

  // Phase 1: Fixed top-level types in priority order
  const topLevelTypes: CascadeNodeType[] = ['course', 'module', 'unit']
  let lastTopLevelIndex = -1

  for (const topType of topLevelTypes) {
    const idx = nodeList.findIndex((n) => n.type === topType)
    if (idx !== -1) {
      result.push(topType)
      seen.add(topType)
      if (idx > lastTopLevelIndex) {
        lastTopLevelIndex = idx
      }
    }
  }

  // Phase 2: Scan forward from lastTopLevelIndex + 1
  const startIndex = lastTopLevelIndex + 1
  for (let i = startIndex; i < nodeList.length; i++) {
    const nodeType = nodeList[i].type

    // Stop at scope boundary (module or unit)
    if (nodeType === 'module' || nodeType === 'unit') {
      break
    }

    // Register valid cascade types not yet seen
    if (
      cascadeTypeSet.has(nodeType) &&
      !seen.has(nodeType as CascadeNodeType)
    ) {
      result.push(nodeType as CascadeNodeType)
      seen.add(nodeType as CascadeNodeType)
    }
  }

  return result
}

/**
 * Extract unique skills list titles from nodes.
 * Excludes titles from question nodes (questions ignore titles).
 * For all other node types, multiple skills list titles are allowed.
 *
 * @param nodes - Array of SheetNodes to extract titles from
 * @returns Sorted array of unique skills list titles (excluding question titles)
 */
export function extractSkillsListTitles(nodes: SheetNode[]): string[] {
  const titles = new Set<string>()
  nodes.forEach((node) => {
    // Skip question nodes - their titles are ignored
    if (node.type === 'question') {
      return
    }

    node.skillsLists.forEach((skillsList) => {
      if (skillsList.title) {
        titles.add(skillsList.title)
      }
    })
    node.newSkillsLists.forEach((skillsList) => {
      if (skillsList.title) {
        titles.add(skillsList.title)
      }
    })
  })
  return Array.from(titles).sort((a, b) => a.localeCompare(b))
}

/**
 * Check if a skills list is valid (matches title and has non-empty skills)
 */
function isValidSkillList(skillsList: SheetSkillsList, title: string): boolean {
  return skillsList.title === title && skillsList.skills.length > 0
}

/**
 * Add skills from from_skills_list to a target node's skills list.
 * Looks for matching title in skillsLists and newSkillsLists.
 * If no match, creates a new skills list entry (nodeid empty).
 * If match exists, adds skills that don't already exist.
 * Modifies the node in place.
 *
 * Title handling rules:
 * - When writing to a question node: always use empty string ('') for title
 * - When writing to any other node type: use the chosen user title parameter
 *
 * Special handling for questions:
 * - For questions, match against any existing skills list (title is ignored when matching)
 * - Uses first skills list found for questions
 * Note: Enforcement of single skills list per question happens during parsing, not here
 */
function addSkillsList(
  targetNode: SheetNode,
  title: string,
  fromSkillsList: SheetSkillsList,
): void {
  const isQuestion = targetNode.type === 'question'

  // Title rule: questions always get empty string, others use the chosen user title
  const effectiveTitle = isQuestion ? '' : title

  // Find first matching skills list
  // For questions: match any skills list (title ignored) - use first one found
  // For others: match by title
  let existingSkillsList: SheetSkillsList | undefined
  if (isQuestion) {
    // For questions: match any skills list (title ignored)
    // Use the first list of any kind, or return undefined if no list exists
    // Enforcement of single skills list per question happens during parsing
    if (targetNode.skillsLists.length > 0) {
      existingSkillsList = targetNode.skillsLists[0]
    } else if (targetNode.newSkillsLists.length > 0) {
      existingSkillsList = targetNode.newSkillsLists[0]
    } else {
      existingSkillsList = undefined
    }
  } else {
    // For others: match by title
    existingSkillsList =
      targetNode.skillsLists.find((sl) => sl.title === effectiveTitle) ||
      targetNode.newSkillsLists.find((sl) => sl.title === effectiveTitle)
  }

  if (!existingSkillsList) {
    // No matching skills list, add a new skills list entry (nodeid empty)
    // For questions: title is always empty (ignored even if source has title)
    targetNode.newSkillsLists.push({
      nodeid: '',
      title: effectiveTitle, // Empty for questions, original title for others
      skills: [...fromSkillsList.skills],
    })
    return
  }

  // Matching entry exists, add skills that don't already exist using Set
  const existingSkillIds = new Set(
    existingSkillsList.skills.map((skill) => skill.skill_id),
  )

  // Find skills to add (skills in fromSkillsList but not in existingSkillsList)
  const skillsToAdd = fromSkillsList.skills.filter(
    (skill) => !existingSkillIds.has(skill.skill_id),
  )

  if (skillsToAdd.length > 0) {
    existingSkillsList.skills.push(...skillsToAdd)
  }
}

/**
 * Check if a "from" node has matching skills for the given title.
 *
 * IMPORTANT: For question types, the title parameter is completely ignored.
 * Questions match any non-empty skills list regardless of title.
 *
 * @param fromNode - The node to check
 * @param title - The skills list title to match (completely ignored for question types)
 * @returns The matching skills list if found, undefined otherwise
 */
function getMatchingSkillsList(
  fromNode: SheetNode,
  title: string,
): SheetSkillsList | undefined {
  // Special case: question types don't have titles, so title matching is ignored
  const isQuestion = fromNode.type === 'question'

  if (isQuestion) {
    // For questions: ignore title parameter completely
    // Find any non-empty skills list (title is not used in matching)
    return (
      fromNode.skillsLists.find((sl) => sl.skills.length > 0) ||
      fromNode.newSkillsLists.find((sl) => sl.skills.length > 0)
    )
  } else {
    // For other types: match by title and ensure skills list has non-empty skills
    const hasValidSkillList =
      fromNode.skillsLists.some((sl) => isValidSkillList(sl, title)) ||
      fromNode.newSkillsLists.some((sl) => isValidSkillList(sl, title))
    if (hasValidSkillList) {
      return (
        fromNode.skillsLists.find((sl) => isValidSkillList(sl, title)) ||
        fromNode.newSkillsLists.find((sl) => isValidSkillList(sl, title))
      )
    }
  }

  return undefined
}

/**
 * Roll up logic: Find all "from" nodes (downstream), then for each find the "to" node upstream.
 * For each "from" node, find the first "to" node that appears before it in the list.
 * Copy standards from "from" to "to".
 */
export function rollUp(
  nodeList: SheetNode[],
  from: string,
  to: string,
  title: string,
): {
  rollup: Array<{
    from_node_id: string
    from_node_title: string
    from_node_type: string
    from_skills_list: SheetSkillsList
    to_node_ids: string[]
  }>
  modifiedNodes: Record<string, SheetNode>
} {
  const entries: Array<{
    from_node_id: string
    from_node_title: string
    from_node_type: string
    from_skills_list: SheetSkillsList
    to_node_ids: string[]
  }> = []

  // Find all "from" nodes (downstream) with matching skills
  for (let i = 0; i < nodeList.length; i++) {
    const fromNode = nodeList[i]
    if (fromNode.type !== from) {
      continue
    }

    // Check if this "from" node has matching skills
    const matchingSkillsList = getMatchingSkillsList(fromNode, title)
    if (!matchingSkillsList) {
      continue
    }

    // Find the FIRST "to" node that appears BEFORE this "from" node (upstream)
    let toNodeId: string | null = null
    for (let j = i - 1; j >= 0; j--) {
      if (nodeList[j].type === to) {
        toNodeId = nodeList[j].node_id
        break // First one found (going backwards)
      }
    }

    if (!toNodeId) {
      continue // No "to" node found upstream
    }

    // Create entry: from (downstream) -> to (upstream)
    const entry: {
      from_node_id: string
      from_node_title: string
      from_node_type: string
      from_skills_list: SheetSkillsList
      to_node_ids: string[]
    } = {
      from_node_id: fromNode.node_id,
      from_node_title: fromNode.title || '',
      from_node_type: fromNode.type || '',
      from_skills_list: matchingSkillsList,
      to_node_ids: [toNodeId], // Single upstream node
    }
    entries.push(entry)
  }

  // Create deep copies of nodes to modify
  const modifiedNodes: Record<string, SheetNode> = {}
  const toNodeIds = new Set<string>()
  entries.forEach((entry) => {
    entry.to_node_ids.forEach((toNodeId) => {
      toNodeIds.add(toNodeId)
    })
  })

  // Create deep copies of "To" nodes to modify
  // Deep copy skillsLists and newSkillsLists, including their skills arrays
  toNodeIds.forEach((toNodeId) => {
    const toNode = nodeList.find((n) => n.node_id === toNodeId)
    if (toNode) {
      modifiedNodes[toNodeId] = {
        ...toNode,
        skillsLists: toNode.skillsLists.map((sl) => ({
          ...sl,
          skills: sl.skills.map((skill) => ({ ...skill })),
        })),
        newSkillsLists: toNode.newSkillsLists.map((sl) => ({
          ...sl,
          skills: sl.skills.map((skill) => ({ ...skill })),
        })),
      }
    }
  })

  // Go through each entry and add skills lists to "To" nodes
  entries.forEach((entry) => {
    entry.to_node_ids.forEach((toNodeId) => {
      const toNode = modifiedNodes[toNodeId]
      if (toNode) {
        addSkillsList(toNode, title, entry.from_skills_list)
      }
    })
  })

  return { rollup: entries, modifiedNodes }
}

/**
 * Roll down logic: Find all "to" nodes (downstream), then for each find the "from" node upstream.
 * For each "to" node, find the last "from" node that appears before it in the list.
 * Copy standards from "from" to "to".
 */
export function rollDown(
  nodeList: SheetNode[],
  from: string,
  to: string,
  title: string,
): {
  rolldown: Array<{
    from_node_id: string
    from_node_title: string
    from_node_type: string
    from_skills_list: SheetSkillsList
    to_node_ids: string[]
  }>
  modifiedNodes: Record<string, SheetNode>
} {
  const entries: Array<{
    from_node_id: string
    from_node_title: string
    from_node_type: string
    from_skills_list: SheetSkillsList
    to_node_ids: string[]
  }> = []

  // Map to track entries by from_node_id (to group multiple "to" nodes per "from" node)
  const entriesByFromNode = new Map<
    string,
    {
      from_node_id: string
      from_node_title: string
      from_node_type: string
      from_skills_list: SheetSkillsList
      to_node_ids: string[]
    }
  >()

  // Find all "to" nodes (downstream)
  for (let i = 0; i < nodeList.length; i++) {
    const toNode = nodeList[i]
    if (toNode.type !== to) {
      continue
    }

    // Find the LAST "from" node that appears BEFORE this "to" node (upstream)
    let fromNode: SheetNode | null = null
    let fromSkillsList: SheetSkillsList | undefined = undefined
    for (let j = i - 1; j >= 0; j--) {
      if (nodeList[j].type === from) {
        fromNode = nodeList[j]
        fromSkillsList = getMatchingSkillsList(fromNode, title)
        if (fromSkillsList) {
          break // Last one found with matching skills (going backwards)
        }
      }
    }

    if (!fromNode || !fromSkillsList) {
      continue // No "from" node with matching skills found upstream
    }

    // Check if we already have an entry for this "from" node
    let entry = entriesByFromNode.get(fromNode.node_id)
    if (!entry) {
      entry = {
        from_node_id: fromNode.node_id,
        from_node_title: fromNode.title || '',
        from_node_type: fromNode.type || '',
        from_skills_list: fromSkillsList,
        to_node_ids: [],
      }
      entriesByFromNode.set(fromNode.node_id, entry)
      entries.push(entry)
    }

    // Add this "to" node to the entry
    entry.to_node_ids.push(toNode.node_id)
  }

  // Create deep copies of nodes to modify
  const modifiedNodes: Record<string, SheetNode> = {}
  const toNodeIds = new Set<string>()
  entries.forEach((entry) => {
    entry.to_node_ids.forEach((toNodeId) => {
      toNodeIds.add(toNodeId)
    })
  })

  // Create deep copies of "To" nodes to modify
  // Deep copy skillsLists and newSkillsLists, including their skills arrays
  toNodeIds.forEach((toNodeId) => {
    const toNode = nodeList.find((n) => n.node_id === toNodeId)
    if (toNode) {
      modifiedNodes[toNodeId] = {
        ...toNode,
        skillsLists: toNode.skillsLists.map((sl) => ({
          ...sl,
          skills: sl.skills.map((skill) => ({ ...skill })),
        })),
        newSkillsLists: toNode.newSkillsLists.map((sl) => ({
          ...sl,
          skills: sl.skills.map((skill) => ({ ...skill })),
        })),
      }
    }
  })

  // Go through each entry and add skills lists to "To" nodes
  entries.forEach((entry) => {
    entry.to_node_ids.forEach((toNodeId) => {
      const toNode = modifiedNodes[toNodeId]
      if (toNode) {
        addSkillsList(toNode, title, entry.from_skills_list)
      }
    })
  })

  return { rolldown: entries, modifiedNodes }
}

/**
 * Calculate cascade skills configuration from cascade parameters
 * Returns null if any required value is missing
 * Supports both "Roll Up" and "Roll Down" modes
 * Uses flat nodeList structure from importData
 */
export function cascadeSkills(
  from: string,
  to: string,
  title: string,
  mode: string,
  importedSheetData: ImportedSheetData | null,
): CascadeData {
  // Return null if any value is missing
  if (!from || !to || !title || !mode) {
    return null
  }

  // If mode is "Off", return null
  if (mode === 'Off') {
    return null
  }

  // Require importedSheetData
  if (
    !importedSheetData ||
    !importedSheetData.nodeList ||
    importedSheetData.nodeList.length === 0
  ) {
    return null
  }

  const { treeId, versionId, nodeList } = importedSheetData

  if (mode === 'Roll Up') {
    const rollUpResult = rollUp(nodeList, from, to, title)
    return {
      from,
      to,
      title,
      mode,
      tree_id: treeId,
      version_id: versionId,
      cascadeResults: {
        rollup: rollUpResult.rollup,
        modifiedNodes: rollUpResult.modifiedNodes,
      },
    }
  } else if (mode === 'Roll Down') {
    const rollDownResult = rollDown(nodeList, from, to, title)
    return {
      from,
      to,
      title,
      mode,
      tree_id: treeId,
      version_id: versionId,
      cascadeResults: {
        rolldown: rollDownResult.rolldown,
        modifiedNodes: rollDownResult.modifiedNodes,
      },
    }
  }

  // Unknown mode
  return null
}

/**
 * Calculate summary rows for cascade results
 */
export function calculateCascadeSummaryRows(
  cascadeData: CascadeData,
): Array<{ cells: (string | number)[] }> {
  if (!cascadeData) {
    return []
  }

  const counts: Record<string, number> = {}
  for (const type of CASCADE_NODE_TYPES) {
    counts[type] = 0
  }

  const cascadeResults = cascadeData.cascadeResults
  let entryCount = 0

  if (cascadeResults) {
    // Count entries
    if ('rollup' in cascadeResults) {
      entryCount = cascadeResults.rollup.length
    } else if ('rolldown' in cascadeResults) {
      entryCount = cascadeResults.rolldown.length
    }

    // Count modified nodes by type
    if ('modifiedNodes' in cascadeResults) {
      Object.values(cascadeResults.modifiedNodes).forEach((node) => {
        if (
          node.type &&
          Object.prototype.hasOwnProperty.call(counts, node.type)
        ) {
          counts[node.type] = (counts[node.type] || 0) + 1
        }
      })
    }
  }

  const modeLabel = cascadeData.mode === 'Roll Up' ? 'rollup' : 'rolldown'

  // Use CASCADE_NODE_TYPES order
  const orderedTypes = CASCADE_NODE_TYPES

  const rows = [
    { cells: ['mode', modeLabel] },
    {
      cells: [`${cascadeDisplayName(cascadeData.from)} nodes read`, entryCount],
    },
    ...orderedTypes.map((type) => ({
      cells: [
        `${cascadeDisplayName(type)} nodes modified`,
        counts[type as CascadeNodeType] || 0,
      ],
    })),
  ]
  return rows
}
